import {
  LoggerFactory,
  ErrorInfo,
  getLogActivityDecorator,
  CustomMetricData,
  MfsStatusCode,
  getLogVerboseDecorator,
  LoggerParams,
} from '@metaos/mfs-telemetry';
import { capitalize } from '../utility';
import { assertPrecondition, Result } from '../errors';

export const logger = LoggerFactory.getInstance().getMfsLogger('client');

/** Logs the given error with the current logger for client package */
export function logError(
  eventName: string,
  operationName: string,
  mfsStatusCode: MfsStatusCode.MfsStatusCode,
  { errorType, message, error }: ErrorInfo,
  logParams?: LoggerParams
): void {
  logger.logError(eventName, mfsStatusCode, { errorType, message, error }, operationName, logParams);
}

/** End activity with failure & throw error if the validation callback fails. */
export function assert(validationCallback: () => Result): void {
  const result = validationCallback();
  if (result.isOK === false) {
    throw result.error;
  }
}

/** Return undefined result from the callback, throw otherwise. */
export function assertGetValue<T>(validationCallback: () => Result<T>): T {
  const result = validationCallback();
  if (result.isOK === false) {
    throw result.error;
  }

  assertPrecondition(result.value !== undefined);
  return result.value;
}

/** Log verbose */
export function logVerbose(operationName: string, message: string, logParams?: LoggerParams): void {
  logger.logVerbose(operationName, message, logParams);
}

/** Log warning */
export function logWarn(
  operationName: string,
  mfsStatusCode: MfsStatusCode.MfsStatusCode,
  { errorType, message, error }: ErrorInfo,
  logParams?: LoggerParams
): void {
  logger.logWarn(
    `${capitalize(operationName)}`,
    mfsStatusCode,
    { errorType, message, error },
    operationName,
    logParams
  );
}

/** Log custom metric */
export function logCustomMetric(
  metricName: string,
  message: string,
  metricData: CustomMetricData,
  operationName: string,
  logParams?: LoggerParams | undefined,
  callTimestamp?: number | undefined
): void {
  logger.logCustomMetric(metricName, message, metricData, operationName, logParams, callTimestamp);
}

/** Log info */
export function logInfo(operationName: string, logParams?: LoggerParams, message?: string): void {
  logger.logInfo(`${capitalize(operationName)}`, message ?? operationName, operationName, logParams);
}

/** A single decorator that works for both sync & async functions */
export const logActivity = getLogActivityDecorator(logger);

/** A single decorator that works for both sync & async functions */
export const logVerboseDecorator = getLogVerboseDecorator(logger);
